<script setup lang="ts">

import { useChatStore } from '@/stores/chatStore.ts'
import { computed } from 'vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import { useAllianceStore } from '@/stores/allianceStore'
import { useAssetStore } from '@/stores/assetStore.ts'
import { CDN } from '@/models/cdn.ts'
import ModalTrigger from '@/components/Interface/Modal/ModalTrigger.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import StyledImage from '@/components/Elements/Images/StyledImage.vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Stores
 */
const chatStore = useChatStore()
const gameStore = useGameStore()
const playerRelationStore = usePlayerRelationStore()
const allianceStore = useAllianceStore()
const assetStore = useAssetStore()
const router = useRouter()

/**
 * Computed
 */
const chatRoom = computed(() => {
  return chatStore.getRoomById(chatStore.activeRoomId)
})

const roomImage = computed(() => {
  if (!chatRoom.value) {
    return ''
  }
  if (chatRoom.value.roomType === 'pm') {
    const players = chatRoom.value.data.players
    // Check which player is not current player (players is an array of player ids)
    const otherPlayerId = players.find((playerId: number) => playerId !== gameStore.player?.id)
    const otherPlayer = playerRelationStore.findRelationByPlayerId(otherPlayerId!)
    return otherPlayer?.profileImage || ''
  } else if (chatRoom.value.roomType === 'alliance') {
    return allianceStore.alliances.get(gameStore.player?.allianceId!)?.allianceImage || ''
  } else {
    return '' // TODO: Add room image for other types of rooms
  }
})

const roomTitle = computed(() => {
  if (!chatRoom.value) {
    return ''
  }
  if(chatRoom.value.roomType === 'pm') {
    const players = chatRoom.value.data.players
    // Check which player is not current player (players is an array of player ids)
    const otherPlayerId = players.find((playerId: number) => playerId !== gameStore.player?.id)
    const otherPlayer = playerRelationStore.findRelationByPlayerId(otherPlayerId!)
    return otherPlayer?.name || ''
  } else if (chatRoom.value.roomType === 'alliance') {
    return allianceStore.alliances.get(gameStore.player?.allianceId!)?.name || ''
  } else {
    return '' // TODO: Add room title for other types of rooms
  }
})

const subTitle = computed(() => {
  if (!chatRoom.value) {
    return ''
  }
  if(chatRoom.value.roomType === 'pm') {
    return t('communication.chat.privateChat')
  } else if (chatRoom.value.roomType === 'alliance') {
    return t('communication.chat.allianceChat')
  } else {
    return '' // TODO: Add room title for other types of rooms
  }
})

// Alliance research data
const currentAlliance = computed(() => {
  if (!chatRoom.value || chatRoom.value.roomType !== 'alliance') {
    return null
  }
  return gameStore.player?.allianceId ? allianceStore.getAlliance(gameStore.player.allianceId) : null
})

const currentAllianceResearch = computed(() => {
  if (!currentAlliance.value?.currentTechId) return null
  return assetStore.findTechById(currentAlliance.value.currentTechId)
})

const allianceResearchPercentage = computed(() => {
  if (!currentAllianceResearch.value || !currentAlliance.value) return 0

  const percentage = Math.floor(
    (currentAlliance.value.sciencePoints / currentAllianceResearch.value.cost) * 100
  )
  return percentage > 100 ? 100 : percentage
})

const isAllianceLeader = computed(() => {
  return currentAlliance.value?.leaderPlayerId === gameStore.player?.id
})

const selectPrototype = async () => {
  // Set the flag for prototype selection mode and store current route to return to
  gameStore.setAlliancePrototypeSelection(true, router.currentRoute.value.fullPath)

  // Navigate to research page
  await router.push('/research')
}

</script>

<template>
  <div v-if="chatRoom" class="chat-header rounded p-2">
    <div class="flex items-center justify-between">
      <div class="flex items-center">
        <div class="w-10 h-10 mr-3 ml-1 shrink-0">
          <StyledImage :src="roomImage" alt="Avatar" size="full" />
        </div>
        <div>
          <p class="text-lg font-semibold truncate w-64 md:w-auto">{{ roomTitle }}</p>
          <p class="text-gray-500">{{ subTitle }}</p>
        </div>
      </div>
      
      <!-- Alliance Research Info (Desktop Only) -->
      <div v-if="chatRoom.roomType === 'alliance' && currentAllianceResearch" class="hidden md:flex items-center">
        <div class="text-right mr-3">
          <div class="text-sm text-metal-300 font-medium">{{ t(`research.techs.${currentAllianceResearch.code}.name`) }}</div>
          <div class="text-xs text-metal-400">{{ t('communication.chat.percentComplete', { percent: allianceResearchPercentage }) }}</div>
        </div>
        <ModalTrigger type="tech" :data="{ techId: currentAllianceResearch.id }">
          <img
            :src="CDN.techIcon(currentAllianceResearch.code)"
            alt="Research Tech"
            class="w-12 h-12 rounded cursor-pointer hover:opacity-80 transition-opacity"
            :title="t('game.alliance.viewResearchDetails', { name: t(`research.techs.${currentAllianceResearch.code}.name`) })"
          />
        </ModalTrigger>
      </div>

      <!-- Set Research Button (Desktop Only, Leader Only) -->
      <div v-else-if="chatRoom.roomType === 'alliance' && !currentAllianceResearch && isAllianceLeader" class="hidden md:flex items-center">
        <ActionButton @click="selectPrototype">
          {{ t('communication.chat.setPrototype') }}
        </ActionButton>
      </div>

      <span v-else class="text-sm text-gray-500"></span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.chat-header {
  background: linear-gradient(180deg, #2f3742 0%, #171b22 100%);
}
</style>